import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Search, ToggleLeft, ToggleRight } from 'lucide-react';

interface User {
  id: string;
  email: string;
  displayName: string;
  companyId?: string;
  company?: {
    name: string;
    logoBase64?: string;
  };
  commissionExempt?: boolean;
  role: string;
  photoBase64?: string;
}

export default function Commissions() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadUsers();
    }
  }, [currentUser]);

  const loadUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersQuery = query(
        usersRef,
        where('role', 'in', ['admin', 'gerente', 'funcionario'])
      );
      
      const snapshot = await getDocs(usersQuery);
      
      const usersData = await Promise.all(
        snapshot.docs.map(async (userDoc) => {
          const userData = userDoc.data();
          let companyData = null;

          if (userData.companyId) {
            try {
              const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
              if (companyDoc.exists()) {
                const company = companyDoc.data();
                companyData = {
                  name: company.commercialName || company.legalName,
                  logoBase64: company.logoBase64
                };
              }
            } catch (err) {
              console.error('Error loading company data:', err);
            }
          }

          return {
            id: userDoc.id,
            email: userData.email || '',
            displayName: userData.displayName || '',
            photoBase64: userData.photoBase64,
            companyId: userData.companyId,
            company: companyData,
            commissionExempt: userData.commissionExempt || false,
            role: userData.role
          };
        })
      );

      // Sort users by role (admin first) and then by name
      const sortedUsers = usersData.sort((a, b) => {
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        return a.displayName.localeCompare(b.displayName);
      });

      setUsers(sortedUsers);
      setError('');
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const toggleCommissionExemption = async (userId: string, currentValue: boolean) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        commissionExempt: !currentValue,
        updatedAt: new Date().toISOString()
      });

      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, commissionExempt: !currentValue }
          : user
      ));
    } catch (err) {
      console.error('Error updating commission exemption:', err);
      setError('Erro ao atualizar isenção de comissão');
    }
  };

  const filteredUsers = users.filter(user =>
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.company?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-secondary mb-6">Gestão de Comissões</h2>

          <div className="mb-6 relative">
            <input
              type="text"
              placeholder="Pesquisar por nome, email ou empresa..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilizador
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.photoBase64 ? (
                          <img
                            src={user.photoBase64}
                            alt={user.displayName}
                            className="h-8 w-8 rounded-full mr-3"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gray-200 mr-3" />
                        )}
                        <span>{user.displayName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.company?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.commissionExempt
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.commissionExempt ? 'Isento' : 'Paga Comissão'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleCommissionExemption(user.id, user.commissionExempt || false)}
                        className="text-primary hover:text-primary-dark flex items-center gap-2"
                      >
                        {user.commissionExempt ? (
                          <>
                            <ToggleRight className="h-5 w-5" />
                            <span>Remover Isenção</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="h-5 w-5" />
                            <span>Isentar</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhum usuário encontrado
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}